import React from "react";
import useClearCart from "api/CartResource/useClearCart";
import useUserCartItems from "api/CartResource/useUserCartItems";
import { Container } from "layouts";
import CartItem from "./CartItem";
import OrderSummary from "./OrderSummary";
import { Link } from "react-router-dom";
import useCurrencyStore from "store/currencyStore";
import PaddingX from "layouts/PaddingX";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BsBookHalf, BsArrowRight } from "react-icons/bs";
import { FiShoppingBag, FiBook, FiArrowLeft } from "react-icons/fi";
import { BookFormat } from "utils/server-constants/bookFormats";
import LoadingCart from "./LoadingCart";
import Price from "pages2/Checkout/Price";
import { getActiveCurrencyCodeLabel } from "utils";

const Cart = () => {
	const { currency } = useCurrencyStore();
	const { data: cart, isLoading: previewIsLoading } = useUserCartItems();
	const { mutateAsync, isLoading: isClearingCart } = useClearCart();
	const [localCart, setLocalCart] = React.useState(null);
	const [showMobileSummary, setShowMobileSummary] = React.useState(false);

	React.useEffect(() => {
		setLocalCart(cart || null);
	}, [cart]);

	const cartItems = React.useMemo(() => localCart?.cart_items || [], [localCart]);
	const hasItems = cartItems.length > 0;
	const currencyLabel = getActiveCurrencyCodeLabel(currency);

	const hasPhysicalItems = cartItems.some((item) => item.format === BookFormat.Physical);
	const hasDigitalItems = cartItems.some((item) => item.format !== BookFormat.Physical);

	const totals = React.useMemo(() => {
		return cartItems.reduce(
			(acc, item) => {
				const quantity = Number(item?.quantity || 0);
				const isPhysical = item?.format === BookFormat.Physical;
				acc.usd +=
					(isPhysical
						? Number(item?.book?.physical_usd_price || 0)
						: Number(item?.book?.price || 0)) * quantity;
				acc.zwl +=
					(isPhysical
						? Number(item?.book?.physical_zwl_price || 0)
						: Number(item?.book?.zwl_price || 0)) * quantity;
				return acc;
			},
			{ usd: 0, zwl: 0 }
		);
	}, [cartItems]);

	const clearCart = async () => {
		await mutateAsync();
		setLocalCart((prev) => ({
			...prev,
			cart_items: [],
		}));
	};
	const handleQuantityChange = React.useCallback((cartId, nextQuantity) => {
		setLocalCart((prev) => {
			if (!prev?.cart_items?.length) {
				return prev;
			}
			return {
				...prev,
				cart_items: prev.cart_items.map((item) =>
					item.id === cartId
						? {
								...item,
								quantity: Number(nextQuantity || 1),
						  }
						: item
				),
			};
		});
	}, []);

	if (previewIsLoading) {
		return <LoadingCart />;
	}

	return (
		<Container>
			<PaddingX>
				<div className={`max-w-7xl mx-auto py-6 sm:py-8 min-h-[calc(100vh-200px)] ${hasItems ? "pb-24 lg:pb-8" : ""}`}>
					{!hasItems ? (
						<div className="min-h-[60vh] flex items-center justify-center py-12">
							<div className="text-center max-w-md mx-auto">
								<div className="relative mb-8">
									<div className="w-32 h-32 mx-auto bg-gradient-to-br from-akgreen/10 to-akgreener/10 rounded-full flex items-center justify-center animate-pulse">
										<FiShoppingBag className="w-16 h-16 text-akgreener/60" />
									</div>
									<div className="absolute -top-2 -right-4">
										<div className="w-10 h-12 bg-blue-100 rounded shadow-sm flex items-center justify-center transform rotate-12">
											<FiBook className="w-5 h-5 text-blue-400" />
										</div>
									</div>
									<div className="absolute -bottom-2 -left-4">
										<div className="w-8 h-10 bg-akgreen/20 rounded shadow-sm flex items-center justify-center transform -rotate-12">
											<FiBook className="w-4 h-4 text-akgreener" />
										</div>
									</div>
								</div>

								<h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">Your cart is empty</h2>
								<p className="text-gray-600 mb-8 max-w-sm mx-auto">
									Looks like you haven&apos;t added any books yet. Discover amazing reads and start
									building your collection.
								</p>

								<Link
									to="/books"
									className="inline-flex items-center gap-2 bg-gradient-to-r from-akgreen to-akgreener
										text-white font-semibold px-8 py-4 rounded-xl shadow-md
										hover:from-akgreener hover:to-akgreenest transition-all duration-300"
								>
									<BsBookHalf className="w-5 h-5" />
									Browse Books
									<BsArrowRight className="w-4 h-4" />
								</Link>
							</div>
						</div>
					) : (
						<div className="space-y-6">
							<div className="flex flex-row justify-between items-start sm:items-center gap-4">
								<div>
									<h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">Shopping Cart</h1>
									<p className="text-sm sm:text-base text-gray-600">
										<span className="text-akgreener font-semibold">{cartItems.length}</span>{" "}
										{cartItems.length === 1 ? "item" : "items"} in your cart
									</p>
									<div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs text-gray-500">
										{hasDigitalItems && (
											<span>Digital books unlock instantly after purchase</span>
										)}
										{hasPhysicalItems && (
											<span>Delivery fee is calculated at checkout for physical books</span>
										)}
									</div>
								</div>

								<button
									type="button"
									className="min-w-max text-sm text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
									onClick={clearCart}
									disabled={isClearingCart}
								>
									{isClearingCart ? (
										<span className="inline-flex items-center gap-2">
											<AiOutlineLoading3Quarters size={14} className="animate-spin" />
											Clearing...
										</span>
									) : (
										"Clear cart"
									)}
								</button>
							</div>

							<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
								<div className="col-span-12 lg:col-span-8 space-y-3">
									{cartItems.map((cartItem) => (
										<CartItem
											key={cartItem.id}
											title={cartItem?.book_title}
											coverUrl={cartItem?.book_cover?.url}
											author={cartItem?.author}
											quantity={cartItem?.quantity}
											book={cartItem?.book}
											format={cartItem?.format}
											cartId={cartItem?.id}
											onQuantityChange={handleQuantityChange}
										/>
									))}

									<div className="pt-4">
										<Link
											to="/books"
											className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-akgreener transition-colors"
										>
											<FiArrowLeft className="w-4 h-4" />
											Continue shopping
										</Link>
									</div>
								</div>

								<div className="col-span-12 lg:col-span-4">
									<div className="lg:sticky lg:top-4">
										<OrderSummary cart={localCart} currency={currency} />
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</PaddingX>

			{hasItems && (
				<>
					<div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 safe-area-inset-bottom">
						<div className="px-4 py-2.5">
							<div className="flex items-center justify-between gap-3">
								<div>
									<p className="text-xs text-gray-500">
										{cartItems.length} item{cartItems.length !== 1 ? "s" : ""}
									</p>
									<p className="text-base font-semibold text-gray-900">
										{currencyLabel} <Price sellable={true} usdPrice={totals.usd} zwlPrice={totals.zwl} isFree={false} />
									</p>
								</div>
								<div className="flex items-center gap-3">
									<button
										onClick={() => setShowMobileSummary(true)}
										className="text-akgreen text-xs font-medium"
									>
										Details
									</button>
									<Link
										to="/checkout"
										className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-akgreener text-white text-sm font-semibold"
									>
										Checkout
									</Link>
								</div>
							</div>
						</div>
					</div>

					{showMobileSummary && (
						<div className="fixed inset-0 bg-black/40 z-50 lg:hidden">
							<div className="absolute inset-0" onClick={() => setShowMobileSummary(false)} />
							<div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl max-h-[75vh] overflow-hidden">
								<div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
									<h2 className="text-sm font-semibold text-gray-900">Order Summary</h2>
									<button
										onClick={() => setShowMobileSummary(false)}
										className="text-gray-400 text-xl leading-none"
									>
										&times;
									</button>
								</div>
								<div className="overflow-y-auto max-h-[calc(75vh-64px)] p-4">
									<OrderSummary
										cart={localCart}
										currency={currency}
										isCompact={true}
										showTrustBadges={false}
									/>
								</div>
							</div>
						</div>
					)}
				</>
			)}
		</Container>
	);
};

export default Cart;
